import React, { FunctionComponent } from "react";
import { useIntl } from "react-intl";
import useCustomizationContext from "../../../../hooks/useCustomizationContext";
import { ReactComponent as IconEdit } from "../../../../icons/edit-2.svg";
import { Panel } from "../../../../states/customization";
import { TemplatePageDefinition } from "../../../../types/customization/template";
import { lowQualityPicturesByPage } from "../../../../utils/customization/box";
import { getCoverDefinitions, getCoverFilteredPages } from "../../../../utils/customization/cover";
import Button from "../../../atoms/Button";
import LowQualityIndicator from "../../../atoms/customization/LowQualityIndicator";
import Page from "../Page";
import "./style.scss";

interface Props {
  onLowQualityWarning?: (PicturesCount: number) => void;
  ppmm: number;
}

const CoverContent: FunctionComponent<Props> = ({ onLowQualityWarning, ppmm }) => {
  const { content, dispatch, selection, template } = useCustomizationContext();
  const intl = useIntl();

  const coverDefinitions = getCoverDefinitions(template.pageDefinitions);

  const backCoverTemplateDefinitions: TemplatePageDefinition[] = [];
  const frontCoverTemplateDefinitions: TemplatePageDefinition[] = [];
  const spineTemplateDefinitions: TemplatePageDefinition[] = [];
  coverDefinitions.forEach((pageDefinition) => {
    if (pageDefinition.type === "front-cover") {
      frontCoverTemplateDefinitions.push(pageDefinition);
    }
    if (pageDefinition.type === "back-cover") {
      backCoverTemplateDefinitions.push(pageDefinition);
    }
    if (pageDefinition.type === "spine") {
      spineTemplateDefinitions.push(pageDefinition);
    }
  });

  // get front and back cover pages
  const frontCoverPages = getCoverFilteredPages(content.pages, frontCoverTemplateDefinitions);
  const backCoverPages = getCoverFilteredPages(content.pages, backCoverTemplateDefinitions);
  const spinePages = getCoverFilteredPages(content.pages, spineTemplateDefinitions);

  const frontCover = frontCoverPages[0];
  const lowQualityPicturesCount = frontCover
    ? lowQualityPicturesByPage(
        frontCover.pageDefinitionKey,
        frontCover.editablePictures,
        selection,
        template
      )
    : 0;
  if (onLowQualityWarning && lowQualityPicturesCount > 0) {
    onLowQualityWarning(lowQualityPicturesCount);
  } else if (onLowQualityWarning && lowQualityPicturesCount === 0) {
    onLowQualityWarning(0);
  }

  const editCoverOnClickHandler = () => {
    dispatch({ type: "setPanel", key: frontCover.key, panel: Panel.EDITCOVER });
  };

  return (
    <div className="CoverContent">
      <div className="CoverContent-cover">
        <div className="CoverContent-cover-head">
          <span className="CoverContent-cover-head-title">
            {intl.formatMessage({
              id: "kustom_CoverPanel_bookTitle_places_cover",
            })}
            {lowQualityPicturesCount > 0 && (
              <LowQualityIndicator
                label={intl.formatMessage({
                  id: "kustom_PageList_LowQuality_some",
                })}
              />
            )}
          </span>
          <span className="CoverContent-cover-head-button">
            <Button type="primary-darker-transparent" onClick={editCoverOnClickHandler}>
              <IconEdit height="16" width="16" />
              {intl.formatMessage({ id: "kustom_CoverContent_customize" })}
            </Button>
          </span>
        </div>
        <div className="CoverContent-cover-pages" onClick={editCoverOnClickHandler}>
          {backCoverPages[0] && (
            <Page
              content={content}
              pageKey={backCoverPages[0].key}
              ppmm={ppmm}
              template={template}
            />
          )}
          <div className="CoverContent-spine">
            {spinePages[0] && (
              <Page content={content} pageKey={spinePages[0].key} ppmm={ppmm} template={template} />
            )}
          </div>
          {frontCover && (
            <Page content={content} pageKey={frontCover.key} ppmm={ppmm} template={template} />
          )}
        </div>
      </div>
    </div>
  );
};

export default CoverContent;
